import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'
import { Testspiel, TeamType } from '../../types'

interface SupabaseTestspielStore {
  testspiele: Testspiel[]
  loading: boolean
  error: string | null
  
  // Actions
  fetchTestspiele: () => Promise<void>
  addTestspiel: (testspiel: Omit<Testspiel, 'id'>) => Promise<{ error?: string }>
  updateTestspielStatus: (id: string, status: 'pending' | 'confirmed' | 'cancelled', comment?: string) => Promise<{ error?: string }>
  deleteTestspiel: (id: string) => Promise<{ error?: string }>
  
  // Helpers
  isDateAvailableForTeam: (team: TeamType, date: string, excludeId?: string) => boolean
}

export const useSupabaseTestspielStore = create<SupabaseTestspielStore>((set, get) => ({
  testspiele: [],
  loading: false,
  error: null,

  fetchTestspiele: async () => {
    set({ loading: true, error: null })
    try {
      const { profile } = useAuthStore.getState()
      if (!profile) throw new Error('Nicht angemeldet')

      console.log('🔍 Fetching testspiele for user:', profile.role, profile.id)

      let query = supabase
        .from('testspiele')
        .select('*')
        .not('team', 'is', null) // Only get entries that have a team (actual testspiele, not available slots)
      
      // Trainer sehen nur ihre eigenen Testspiele, Admins sehen alle
      if (profile.role === 'trainer') {
        console.log('👨‍🏫 Trainer mode: filtering by user_id')
        query = query.eq('user_id', profile.id)
      } else {
        console.log('👑 Admin mode: fetching all testspiele')
      }
      
      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) {
        console.error('❌ Database error:', error)
        throw error
      }

      console.log('📊 Raw data from database:', data?.length, 'records')

      // Daten in das erwartete Format konvertieren
      const testspiele: Testspiel[] = (data || []).map(item => ({
        id: item.id,
        team: item.team as TeamType,
        opponent: item.opponent ?? '',
        date: item.date,
        time: item.time,
        trainerName: item.trainer_name ?? '',
        location: item.location ?? undefined,
        isHome: item.is_home ?? undefined,
        notes: item.notes ?? undefined,
        status: item.status === 'available' ? 'pending' : item.status,
        contactPerson: item.contact_person ?? undefined,
        contactEmail: item.contact_email ?? undefined,
        contactPhone: item.contact_phone ?? undefined,
      }))

      console.log('✅ Processed testspiele:', testspiele.length, 'records')
      console.log('📋 Testspiele by status:', {
        pending: testspiele.filter(t => t.status === 'pending').length,
        confirmed: testspiele.filter(t => t.status === 'confirmed').length,
        cancelled: testspiele.filter(t => t.status === 'cancelled').length
      })

      set({ testspiele, loading: false })
    } catch (error: any) {
      console.error('❌ fetchTestspiele error:', error)
      set({ error: error.message, loading: false })
    }
  },

  addTestspiel: async (testspiel) => {
    try {
      const { user } = useAuthStore.getState()
      if (!user) return { error: 'Nicht angemeldet' }

      // Check if there's an available slot for this date/time
      const { data: existingSlot, error: fetchError } = await supabase
        .from('testspiele')
        .select('*')
        .eq('date', testspiel.date)
        .eq('time', testspiel.time)
        .eq('status', 'available')
        .single()

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "no rows found"
        throw fetchError
      }

      if (existingSlot) {
        // Update existing available slot to become a testspiel
        const { error } = await supabase
          .from('testspiele')
          .update({
            team: testspiel.team,
            opponent: testspiel.opponent,
            trainer_name: testspiel.trainerName,
            location: testspiel.location,
            is_home: testspiel.isHome || true,
            notes: testspiel.notes,
            status: testspiel.status || 'pending',
            contact_person: testspiel.contactPerson,
            contact_email: testspiel.contactEmail,
            contact_phone: testspiel.contactPhone,
            user_id: user.id,
          })
          .eq('id', existingSlot.id)

        if (error) throw error
      } else {
        // Create new entry if no available slot exists
        const { error } = await supabase
          .from('testspiele')
          .insert({
            date: testspiel.date,
            time: testspiel.time,
            status: testspiel.status || 'pending',
            team: testspiel.team,
            opponent: testspiel.opponent,
            trainer_name: testspiel.trainerName,
            location: testspiel.location,
            is_home: testspiel.isHome || true,
            notes: testspiel.notes,
            contact_person: testspiel.contactPerson,
            contact_email: testspiel.contactEmail,
            contact_phone: testspiel.contactPhone,
            user_id: user.id,
          })

        if (error) throw error
      }

      // Testspiele neu laden
      get().fetchTestspiele()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  updateTestspielStatus: async (id, status, comment) => {
    try {
      const currentTestspiel = get().testspiele.find(t => t.id === id)
      const updatedNotes = comment && currentTestspiel
        ? `${currentTestspiel.notes || ''}\n\nStatus-Update: ${comment}`.trim()
        : currentTestspiel?.notes

      const { error } = await supabase
        .from('testspiele')
        .update({ 
          status,
          notes: updatedNotes
        })
        .eq('id', id)

      if (error) throw error

      // Testspiele neu laden
      get().fetchTestspiele()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  deleteTestspiel: async (id) => {
    try {
      // Instead of deleting, convert back to available slot
      const { error } = await supabase
        .from('testspiele')
        .update({
          status: 'available',
          team: null,
          opponent: null,
          trainer_name: null,
          location: null,
          is_home: null,
          notes: null,
          contact_person: null,
          contact_email: null,
          contact_phone: null,
          user_id: null,
        })
        .eq('id', id)

      if (error) throw error

      // Testspiele neu laden
      get().fetchTestspiele()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  isDateAvailableForTeam: (team, date, excludeId) => {
    const { testspiele } = get()
    return !testspiele.some(testspiel => 
      testspiel.team === team && 
      testspiel.date === date && 
      (!excludeId || testspiel.id !== excludeId)
    )
  },
}))
